import React, { useState, useEffect } from 'react';
import { Space, Input, Button, InputNumber, Upload } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { getFirestore, collection, addDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { showSuccessNotification, showWarningNotification, showErrorNotification } from '../../../utils/notifications';
import MediumModal from '../../../components/Components/Modals/MediumModal';

function CreateNewBadge({
    isVisible,
    onClose,
    productId,
    productData
}) {
    const [newBadgeData, setNewBadgeData] = useState({
        background_color: "#000000", // Nero
        group: "Featured",
        icon: "",
        name: "",
        short_name: "",
        text_color: "#ffffff", // Bianco
        type: "tag"
    });

    const [currentProductData, setCurrentProductData] = useState(productData);

    const db = getFirestore();

    // Aggiorna i dati del prodotto quando cambiano
    useEffect(() => {
        setCurrentProductData(productData);
    }, [productData]);

    // Configurazione per l'upload delle icone dei badge
    const uploadProps = {
        beforeUpload: (file) => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                showErrorNotification("Error", "You can only upload image files!");
                return false;
            }

            // Converti l'immagine in base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setNewBadgeData(prev => ({ ...prev, icon: reader.result }));
            };
            return false; // Previene l'upload automatico
        },
        maxCount: 1,
    };

    // Funzione per gestire la creazione di un nuovo badge
    const handleCreateBadge = async () => {
        try {
            // Validazione
            if (!newBadgeData.name) {
                showWarningNotification("Attention", "The name of the badge is required");
                return;
            }

            // 1. Crea il nuovo Tags
            const newBadgeRef = await addDoc(collection(db, "Tags"), {
                ...newBadgeData,
                group: "Featured", // Assicuriamoci che sia sempre "Featured"
                type: "tag" // Assicuriamoci che sia sempre "tag"
            });

            // 2. Ottieni i dati più recenti del prodotto
            const productRef = doc(db, "Products", productId);
            const productSnapshot = await getDoc(productRef);

            if (productSnapshot.exists()) {
                const freshProductData = productSnapshot.data();
                const currentCategories = freshProductData.list_tags || [];

                // Aggiungi l'ID del nuovo badge alla list_tags del prodotto
                const updatedCategories = [...currentCategories, newBadgeRef.id];
                await updateDoc(productRef, { list_tags: updatedCategories });

                showSuccessNotification("Save successful", "Badge created and added successfully!");
            } else {
                showErrorNotification("Error", "Product not found");
                return;
            }

            // Resetta il form e chiudi la modale
            resetForm();
            onClose(true);
        } catch (error) {
            console.error("Error in creating the badge:", error);
            showErrorNotification("Error", "Error in creating the badge");
        }
    };

    // Funzione per resettare il form
    const resetForm = () => {
        setNewBadgeData({
            background_color: "#000000",
            group: "Featured",
            icon: "",
            name: "",
            short_name: "",
            text_color: "#ffffff",
            type: "tag"
        });
    };

    // Funzione per gestire la chiusura della modale
    const handleModalClose = () => {
        resetForm();
        onClose(false);
    };

    return (
        <MediumModal
            title="Create New Badge"
            visible={isVisible}
            onClose={handleModalClose}
            onConfirm={handleCreateBadge}
            confirmText="Create"
            cancelText="Cancel"
            width={700}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                    <label>Name:</label>
                    <Input
                        value={newBadgeData.name}
                        onChange={e => setNewBadgeData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Insert badge name"
                    />
                </div>

                <div>
                    <label>Short Name:</label>
                    <Input
                        value={newBadgeData.short_name}
                        onChange={e => setNewBadgeData(prev => ({ ...prev, short_name: e.target.value }))}
                        placeholder="Insert short name or leave empty"
                    />
                </div>



                <div>
                    <label>Icon:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Upload {...uploadProps} showUploadList={false}>
                            <Button icon={<UploadOutlined />}>Carica Icona</Button>
                        </Upload>
                        {newBadgeData.icon && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <img
                                    src={newBadgeData.icon}
                                    alt="Icon preview"
                                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                />
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => setNewBadgeData(prev => ({ ...prev, icon: '' }))}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label>Background Color:</label>
                    <Input
                        type="color"
                        value={newBadgeData.background_color}
                        onChange={e => setNewBadgeData(prev => ({ ...prev, background_color: e.target.value }))}
                    />
                </div>

                <div>
                    <label>Text Color:</label>
                    <Input
                        type="color"
                        value={newBadgeData.text_color}
                        onChange={e => setNewBadgeData(prev => ({ ...prev, text_color: e.target.value }))}
                    />
                </div>
            </Space>
        </MediumModal>
    );
}

export default CreateNewBadge; 
